import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { Input, Label, RadioGroup, SearchableSelect, TagInput, useDebouncedValue, } from '@wit-support/ui';
import { CountryPicker } from '../country-picker.js';
import { formatCountryListWithin } from '../lib/country.js';
import { useAdsTargetingData } from './data.js';
import { META_SEARCH_MIN_CHARS, buildInterestSuggestions, inAdSetsLabel, } from './interest-suggestions.js';
/**
 * Виджеты таргетинга — общие для «Запусков» (форма шаблона/запуска) и «Кампаний» (правка живой
 * группы). Перенесены из `admin-module-ads-launcher/registry/field-widget.tsx` без изменения
 * поведения; справочники и оболочка поля приходят из `AdsTargetingDataProvider`.
 */
function shellProps(p) {
    return {
        meta: p.meta,
        fieldState: p.fieldState,
        label: p.label,
        description: p.description,
        hint: p.hint,
        htmlFor: p.field.name,
    };
}
// --- возраст и прочие «от–до» ----------------------------------------------
export function IntRangeWidget(p) {
    const { FieldShell } = useAdsTargetingData();
    const v = (p.field.value ?? {});
    const set = (k, raw) => {
        const n = raw === '' ? undefined : Number(raw);
        p.field.onChange({ ...v, [k]: n });
    };
    return (_jsx(FieldShell, { ...shellProps(p), children: _jsxs("div", { className: "flex items-center gap-2", children: [_jsx(Input, { id: p.field.name, type: "number", "aria-label": "\u041E\u0442", className: "w-24", value: v.min ?? '', disabled: p.disabled, onBlur: p.field.onBlur, onChange: (e) => set('min', e.target.value) }), _jsx("span", { className: "text-[var(--color-text-muted)]", children: "\u2013" }), _jsx(Input, { type: "number", "aria-label": "\u0414\u043E", className: "w-24", value: v.max ?? '', disabled: p.disabled, onBlur: p.field.onBlur, onChange: (e) => set('max', e.target.value) })] }) }));
}
export function GeoWidget(p) {
    const { FieldShell } = useAdsTargetingData();
    const v = (p.field.value ?? { mode: 'WORLDWIDE' });
    const options = p.meta.adsOptions ?? [
        { value: 'WORLDWIDE', label: 'Весь мир' },
        { value: 'COUNTRIES', label: 'Страны' },
    ];
    const countries = v.mode === 'COUNTRIES' ? v.countries : [];
    return (_jsx(FieldShell, { ...shellProps(p), children: _jsxs("div", { className: "flex flex-col gap-2", children: [_jsx(RadioGroup, { name: p.field.name, value: v.mode, disabled: p.disabled, onValueChange: (mode) => p.field.onChange(mode === 'WORLDWIDE' ? { mode } : { mode, countries }), className: "flex flex-wrap gap-4", children: options.map((o) => {
                        const id = `${p.field.name}__${o.value}`;
                        return (_jsxs("div", { className: "flex items-center gap-2", children: [_jsx(RadioGroup.Item, { value: o.value, id: id }), _jsx(Label, { htmlFor: id, children: o.label })] }, o.value));
                    }) }), v.mode === 'COUNTRIES' ? (
                // хранится как раньше — ISO-коды; ввод — поиск по названию или коду,
                // вставка «DE, FR, IT» по-прежнему работает
                _jsx(CountryPicker, { "aria-label": "\u041F\u043E\u0438\u0441\u043A \u0441\u0442\u0440\u0430\u043D\u044B", value: countries, disabled: p.disabled === true, onChange: (next) => p.field.onChange({ mode: 'COUNTRIES', countries: next }) })) : null] }) }));
}
// --- слоты аудиторий ---------------------------------------------------------
/** Предел имени слота auto-ads. */
const SLOT_NAME_MAX = 60;
/**
 * Слоты аудиторий шаблона — по ИМЕНИ: «LAL 1 %», «Покупатели год». Какая
 * аудитория стоит за именем, решает профиль каждого кабинета. Подсказки — все
 * имена из профилей кабинетов; можно ввести новое и привязать его в профиле.
 */
export function AudienceSlotsWidget(p) {
    const { audienceSlotNames, FieldShell } = useAdsTargetingData();
    const value = (Array.isArray(p.field.value) ? p.field.value : []);
    const [query, setQuery] = useState('');
    const q = query.trim().toLocaleLowerCase('ru-RU');
    const known = audienceSlotNames ?? [];
    const suggestions = known
        .filter((n) => q === '' || n.toLocaleLowerCase('ru-RU').includes(q))
        .map((n) => ({ id: n, name: n }));
    return (_jsx(FieldShell, { ...shellProps(p), children: _jsxs("div", { className: "flex flex-col gap-1", children: [_jsx(TagInput, { "aria-label": typeof p.label === 'string' ? p.label : 'Слоты аудиторий', value: value, suggestions: suggestions, query: query, onQueryChange: setQuery, disabled: p.disabled === true, placeholder: "\u0418\u043C\u044F \u0441\u043B\u043E\u0442\u0430\u2026", maxLength: SLOT_NAME_MAX, maxTags: 50, createLabel: (name) => `Новый слот «${name}»`, emptyLabel: "\u0421\u043B\u043E\u0442\u043E\u0432 \u0441 \u0442\u0430\u043A\u0438\u043C \u0438\u043C\u0435\u043D\u0435\u043C \u043D\u0435\u0442 \u2014 \u0432\u0432\u0435\u0434\u0438\u0442\u0435 \u043D\u043E\u0432\u043E\u0435", onChange: (next) => p.field.onChange(next.map((n) => n.trim()).filter((n) => n !== '')) }), _jsx("span", { className: "text-xs text-[var(--color-text-muted)]", children: known.length > 0
                        ? `Имена из профилей кабинетов: ${known.join(', ')}. В каждом кабинете запуска имя должно быть привязано к аудитории — иначе ошибка при проверке.`
                        : 'В профилях кабинетов слотов пока нет. Введите имя и привяжите его к аудитории в профиле каждого кабинета («Кабинеты» → профиль → «Слоты аудиторий»).' })] }) }));
}
/**
 * Интересы: на пустом вводе — «Уже использовали» (интересы наших групп во всех
 * кабинетах, по числу групп); при вводе — они же, отфильтрованные локально, и
 * ниже «Ещё из Meta» (поиск по справочнику Meta от 2 символов, без повторов).
 * Выбирать можно из обеих секций; в Meta уходят только id.
 */
export function InterestsWidget(p) {
    const { platformAccountId, useUsedInterests, useInterestSearch, FieldShell } = useAdsTargetingData();
    const value = (Array.isArray(p.field.value) ? p.field.value : []);
    const [query, setQuery] = useState('');
    const q = useDebouncedValue(query.trim(), 300);
    const used = useUsedInterests();
    const metaEnabled = platformAccountId != null && q.length >= META_SEARCH_MIN_CHARS;
    const search = useInterestSearch(platformAccountId, q, metaEnabled);
    // ответ Meta — только когда отложенный запрос догнал ввод: в паузу 300 мс выдача прошлого
    // запроса не показывается и не поднимает чужой интерес в «Уже использовали» (через совпадение id)
    const metaItems = metaEnabled && q === query.trim() ? search.items : [];
    const suggestions = buildInterestSuggestions({
        used: used.items,
        meta: metaItems,
        query,
    });
    const usageById = new Map(suggestions.map((s) => [s.id, s.usageCount]));
    const names = new Map(value.map((i) => [i.id, i.name]));
    for (const s of suggestions)
        if (!names.has(s.id))
            names.set(s.id, s.name);
    const shortQuery = query.trim().length > 0 && query.trim().length < META_SEARCH_MIN_CHARS;
    return (_jsx(FieldShell, { ...shellProps(p), children: _jsxs("div", { className: "flex flex-col gap-1", children: [_jsx(TagInput, { identity: "id", allowCreate: false, "aria-label": typeof p.label === 'string' ? p.label : 'Интересы', value: value.map((i) => i.id), getLabel: (id) => names.get(id) ?? id, suggestions: suggestions, renderSuggestion: (s) => {
                        const n = usageById.get(s.id);
                        return (_jsxs(_Fragment, { children: [_jsx("span", { className: "truncate", children: s.name }), n != null ? (_jsx("span", { className: "shrink-0 text-xs text-[var(--color-text-muted)]", children: inAdSetsLabel(n) })) : null] }));
                    }, query: query, onQueryChange: setQuery, 
                    // «Загрузка…» заменяет весь список — только когда показать ещё нечего
                    loading: suggestions.length === 0 && (used.isFetching || search.isFetching), disabled: p.disabled || platformAccountId == null, placeholder: "\u041D\u0430\u0439\u0442\u0438 \u0438\u043D\u0442\u0435\u0440\u0435\u0441\u2026", maxTags: 25, emptyLabel: shortQuery
                        ? 'Среди уже использованных нет — для поиска в Meta введите ещё букву'
                        : 'Ничего не найдено', onChange: (ids) => p.field.onChange(ids.map((id) => ({ id, name: names.get(id) ?? id }))) }), platformAccountId == null ? (_jsx("span", { className: "text-xs text-[var(--color-text-muted)]", children: "\u041F\u043E\u0438\u0441\u043A \u0438\u043D\u0442\u0435\u0440\u0435\u0441\u043E\u0432 \u2014 \u043F\u043E\u0441\u043B\u0435 \u0432\u044B\u0431\u043E\u0440\u0430 \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u0430." })) : null] }) }));
}
// --- список исключаемых стран ------------------------------------------------
const NONE = '__none__';
/**
 * Список исключаемых стран: явно выбранный список или «Не исключать» (по
 * умолчанию). Неявного списка «по умолчанию» нет (v3 §3).
 */
export function CountryListWidget(p) {
    const { countryLists, FieldShell } = useAdsTargetingData();
    const raw = p.field.value;
    const current = raw == null || raw === '' ? NONE : raw;
    return (_jsx(FieldShell, { ...shellProps(p), children: _jsx(SearchableSelect, { id: p.field.name, value: current, onChange: (v) => p.field.onChange(v === NONE ? null : v), disabled: p.disabled === true, placeholder: "\u0412\u044B\u0431\u0435\u0440\u0438\u0442\u0435 \u0441\u043F\u0438\u0441\u043E\u043A\u2026", loading: countryLists == null, options: [
                { value: NONE, label: 'Не исключать' },
                ...(countryLists ?? []).map((l) => {
                    const { text, full } = formatCountryListWithin(l.countries);
                    return {
                        value: l.id,
                        label: `${l.name} (${l.countries.length})`,
                        description: text === '' ? 'Пустой список' : text,
                        title: full,
                    };
                }),
            ] }) }));
}
//# sourceMappingURL=widgets.js.map